import React, { useCallback, useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";

import SimpleBar from "simplebar-react";
import withRouter from "../Common/withRouter";
import { Link } from "react-router-dom";
import { buildScopedPath } from "../../utils/account-context";
import { useActiveAccountId } from "../../context/account-branding-context";
import { setIsSuperAdminView as setSuperAdminViewPreference } from "../../utils/view-mode";
import { useAccountFeatureToggle } from "../../hooks/useAccountFeatureToggle";
import { useSaasEntitlements } from "../../hooks/useSaasEntitlements";
import TvPremiumCrown from "../saas/TvPremiumCrown";
import { getVisibleSections } from "./sidebarMenuConfig";
import { isSidebarItemActive } from "./sidebarActivePath";
import { isSaasFreePlan } from "../../utils/saasGenerateGate";
import { useCreateDesign } from "../../context/create-design-context";
import {
  TvAnchor,
  TvDiv,
  TvI,
  TvLi,
  TvSpan,
  TvUl,
} from "@trustvid/design-system";
import { withTranslation } from "react-i18next";

const APP_ENV = process.env.REACT_APP_ENV || "dev";

const SidebarContent = (props) => {
  const ref = useRef();
  const location = useLocation();
  const userData = JSON.parse(localStorage.getItem("userdata"));
  const role = userData?.role;
  const activeAccountId = useActiveAccountId();
  const accountId = activeAccountId || userData?.account?.id || userData?.account_id;
  const { isEnabled: workflowsEnabled } = useAccountFeatureToggle(accountId, "workflows");
  const { isEnabled: trustvidCrmEnabled } = useAccountFeatureToggle(accountId, "trustvid_crm");
  const { isEnabled: shopifyEnabled } = useAccountFeatureToggle(accountId, "shopify");
  const saasEntitlementsState = useSaasEntitlements(accountId);
  const saasEntitlements = saasEntitlementsState?.entitlements || { isPremium: false };
  const isFreePlan = isSaasFreePlan(saasEntitlements);
  const { openCreateDesign, isOpen: isCreateDesignOpen } = useCreateDesign();

  const enabledFeatureKeys = useMemo(
    () => [
      workflowsEnabled ? "workflows" : null,
      trustvidCrmEnabled ? "trustvid_crm" : null,
      shopifyEnabled ? "shopify" : null,
    ].filter(Boolean),
    [workflowsEnabled, trustvidCrmEnabled, shopifyEnabled]
  );

  const sections = useMemo(
    () =>
      getVisibleSections(role, APP_ENV, enabledFeatureKeys, {
        plan: saasEntitlements.plan,
        isPremium: saasEntitlements.isPremium,
      }),
    [role, enabledFeatureKeys, saasEntitlements.plan, saasEntitlements.isPremium]
  );

  const getItemPath = useCallback(
    (item) => (item.scoped ? buildScopedPath(accountId, item.to) : item.to),
    [accountId]
  );

  const menuItemPaths = useMemo(
    () =>
      sections
        .flatMap((section) => section.items || [])
        .filter((item) => item?.to && item.type !== "external" && !item.action)
        .map((item) => getItemPath(item))
        .filter(Boolean),
    [getItemPath, sections]
  );

  const isItemActive = (item) => {
    if (item.type === "external" || item.action || !item.to) {
      return false;
    }
    const path = getItemPath(item);
    if (!path) {
      return false;
    }
    return isSidebarItemActive({
      pathname: location.pathname,
      itemPath: path,
      siblingPaths: menuItemPaths.filter((otherPath) => otherPath !== path),
    });
  };

  useEffect(() => {
    if (role === "SuperAdmin") {
      setSuperAdminViewPreference(true);
    }
  }, [role]);

  useEffect(() => {
    ref.current?.recalculate();
  }, [props.isCollapsed]);

  useEffect(() => {
    const recalculateSidebarScroll = () => {
      ref.current?.recalculate();
    };
    const observer = new MutationObserver(recalculateSidebarScroll);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const menu = document.getElementById("side-menu");
    if (!menu) {
      return undefined;
    }

    let activeFlyoutAnchor = null;

    const updateCollapsedFlyoutPosition = (anchor) => {
      if (!document.body.classList.contains("vertical-collpsed") || !anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      anchor.style.setProperty("--sidebar-flyout-top", `${rect.top}px`);
    };

    const handlePointerOver = (event) => {
      const anchor = event.target.closest("#side-menu > li > a");
      if (!anchor || !menu.contains(anchor)) {
        return;
      }
      activeFlyoutAnchor = anchor;
      updateCollapsedFlyoutPosition(anchor);
    };

    const handleScroll = () => {
      updateCollapsedFlyoutPosition(activeFlyoutAnchor);
    };

    const handleMenuLeave = () => {
      activeFlyoutAnchor = null;
    };

    const scrollElement = ref.current?.getScrollElement?.();
    menu.addEventListener("pointerover", handlePointerOver);
    menu.addEventListener("mouseleave", handleMenuLeave);
    if (scrollElement && typeof scrollElement.addEventListener === "function") {
      scrollElement.addEventListener("scroll", handleScroll, { passive: true });
    }

    return () => {
      menu.removeEventListener("pointerover", handlePointerOver);
      menu.removeEventListener("mouseleave", handleMenuLeave);
      if (scrollElement && typeof scrollElement.removeEventListener === "function") {
        scrollElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  const renderItem = (item) => {
    const label = props.t(item.labelKey);
    const active =
      item.action === "createDesign" ? isCreateDesignOpen : isItemActive(item);
    const linkClass = [
      "tv-saas-nav__link",
      active ? "is-active active" : "",
      item.premiumLocked && isFreePlan ? "is-locked" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const crownTitle = item.crownTitle || `${item.labelKey} — Pay as you go`;
    const inner = (
      <>
        <TvI className={item.icon} />
        <TvSpan className="tv-saas-nav__label">{label}</TvSpan>
        {item.premiumLocked && isFreePlan ? (
          <TvPremiumCrown className="tv-saas-nav__crown" title={crownTitle} />
        ) : null}
        {item.adminBadge ? (
          <TvSpan className="tv-saas-nav__badge">Admin</TvSpan>
        ) : null}
      </>
    );

    if (item.type === "external") {
      return (
        <TvLi key={item.id}>
          <TvAnchor href={item.href} target="_blank" rel="noreferrer" className={linkClass} title={label}>
            {inner}
          </TvAnchor>
        </TvLi>
      );
    }

    if (item.action === "createDesign") {
      return (
        <TvLi key={item.id}>
          <TvAnchor
            href="#"
            className={linkClass}
            title={label}
            data-testid="sidebar-create-design"
            onClick={(e) => {
              e.preventDefault();
              openCreateDesign();
            }}
          >
            {inner}
          </TvAnchor>
        </TvLi>
      );
    }

    return (
      <TvLi key={item.id}>
        <Link to={getItemPath(item)} className={linkClass} title={label}>
          {inner}
        </Link>
      </TvLi>
    );
  };

  return (
    <SimpleBar className="h-100" ref={ref}>
      <TvDiv id="sidebar-menu" className="tv-saas-nav d-flex flex-column">
        <TvUl className="list-unstyled mb-0" id="side-menu">
          {sections.map((section) => (
            <React.Fragment key={section.id}>
              {section.showSectionHeader && section.titleKey ? (
                <TvLi className={`tv-saas-nav__section${section.footer ? " tv-saas-nav__section--foot" : ""}`}>
                  {props.t(section.titleKey)}
                </TvLi>
              ) : null}
              {section.items.map(renderItem)}
            </React.Fragment>
          ))}
        </TvUl>
      </TvDiv>
    </SimpleBar>
  );
};

SidebarContent.propTypes = {
  location: PropTypes.object,
  t: PropTypes.any,
  isCollapsed: PropTypes.bool,
};

export default withRouter(withTranslation()(SidebarContent));
